import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, AlertTriangle, Trash2, RotateCcw } from "lucide-react";

/**
 * Manager-only compact modal to fix a single historical movement's qty
 * and/or unit_cost. Powers the pencil action on the History page and the
 * SupplierSpendDetailDialog drill-down. Backend PUT /movements/{mid} also
 * adjusts stock.current_qty by the qty delta (respecting IN/OUT sign) so
 * live inventory stays honest after a correction.
 */
const money = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "—"
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MovementFixDialog = ({ open, movement, onClose, onSaved }) => {
  const [qty, setQty] = useState("");
  const [cost, setCost] = useState("");
  const [when, setWhen] = useState("");
  const [hours, setHours] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open && movement) {
      setQty(movement.qty === null || movement.qty === undefined ? "" : String(movement.qty));
      setCost(
        movement.unit_cost === null || movement.unit_cost === undefined
          ? ""
          : String(movement.unit_cost),
      );
      // Prefill the date input from created_at (YYYY-MM-DD slice keeps the
      // native <input type=date> happy across browsers).
      setWhen((movement.created_at || "").slice(0, 10));
      setHours(
        movement.machine_hours === null || movement.machine_hours === undefined
          ? ""
          : String(movement.machine_hours),
      );
    }
  }, [open, movement]);

  const originalLine = useMemo(() => {
    if (!movement) return 0;
    return Number(movement.qty || 0) * Number(movement.unit_cost || 0);
  }, [movement]);

  const nextLine = useMemo(() => {
    const q = Number(qty);
    const c = Number(cost);
    if (Number.isNaN(q) || Number.isNaN(c)) return null;
    return q * c;
  }, [qty, cost]);

  const qtyChanged = movement && Number(qty) !== Number(movement.qty);
  const costChanged = movement && Number(cost) !== Number(movement.unit_cost);
  const dateChanged = movement && when && when !== (movement.created_at || "").slice(0, 10);
  const originalHours = movement?.machine_hours ?? "";
  const hoursChanged = movement && String(hours) !== String(originalHours);

  const submit = async () => {
    if (!movement) return;
    const q = Number(qty);
    const c = Number(cost);
    if (Number.isNaN(q) || q <= 0) {
      toast.error("Qty must be > 0");
      return;
    }
    if (Number.isNaN(c) || c < 0) {
      toast.error("Unit cost must be ≥ 0");
      return;
    }
    if (!qtyChanged && !costChanged && !dateChanged && !hoursChanged) {
      toast.info("Nothing changed");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (qtyChanged) payload.qty = q;
      if (costChanged) payload.unit_cost = c;
      if (dateChanged) payload.created_at = when;
      if (hoursChanged) payload.machine_hours = hours === "" ? "" : Number(hours);
      const { data } = await api.put(`/movements/${movement.id}`, payload);
      toast.success("Movement updated");
      onSaved?.({ ...movement, ...data });
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const deleteMovement = async () => {
    if (!movement) return;
    const direction = movement.action === "IN" ? "IN" : "OUT";
    const reversal =
      direction === "IN"
        ? `remove ${movement.qty} ${movement.unit} from ${movement.stock_name}'s stock (reversing this receive)`
        : `return ${movement.qty} ${movement.unit} back to ${movement.stock_name}'s stock (reversing this take-out)`;
    const spendReversal =
      direction === "IN" && Number(movement.unit_cost || 0) > 0
        ? ` and remove ${money(Number(movement.qty || 0) * Number(movement.unit_cost || 0))} from spend reports`
        : "";
    const ok = window.confirm(
      `Delete this ${direction} movement?\n\nThis will:\n • ${reversal}${spendReversal}\n • Log the deletion under your account\n\nYou can restore it later from the History page.`,
    );
    if (!ok) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/movements/${movement.id}`);
      const onHand = data?.stock_current_qty;
      if (onHand !== undefined && onHand !== null) {
        toast.success(
          `Movement deleted. ${movement.stock_name} on-hand is now ${onHand} ${movement.unit}.`,
          {
            description:
              "If that's wrong, open History → Show deleted → pencil → Restore.",
            duration: 8000,
          },
        );
      } else {
        toast.success("Movement deleted");
      }
      onSaved?.({ ...movement, ...data });
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    } finally {
      setDeleting(false);
    }
  };

  const restoreMovement = async () => {
    if (!movement) return;
    setDeleting(true);
    try {
      const { data } = await api.post(`/movements/${movement.id}/restore`);
      const onHand = data?.stock_current_qty;
      if (onHand !== undefined && onHand !== null) {
        toast.success(
          `Movement restored. ${movement.stock_name} on-hand is now ${onHand} ${movement.unit}.`,
          { duration: 6000 },
        );
      } else {
        toast.success("Movement restored");
      }
      onSaved?.({ ...movement, ...data });
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not restore");
    } finally {
      setDeleting(false);
    }
  };

  if (!open || !movement) return null;

  const direction = movement.action === "IN" ? "received" : "taken out";

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="movement-fix-dialog"
    >
      <div className="bg-white border-4 border-black rounded-3xl shadow-[8px_8px_0px_0px_#000] w-full max-w-lg">
        <div className="p-5 border-b-2 border-black bg-gradient-to-r from-[#0D1F3C] to-[#1E3C66] text-white rounded-t-[22px] flex items-start justify-between gap-2">
          <div>
            <div className="label-eyebrow opacity-80">Fix historical movement</div>
            <div className="font-display text-2xl leading-tight">{movement.stock_name}</div>
            <div className="text-xs opacity-80 mt-1">
              {movement.action} — {direction} on{" "}
              {new Date(movement.created_at).toLocaleDateString([], {
                dateStyle: "medium",
              })}
            </div>
          </div>
          <button
            data-testid="movement-fix-close"
            onClick={onClose}
            className="nb-btn bg-white p-2 shrink-0"
            aria-label="close"
          >
            <X size={16} strokeWidth={2.5} className="text-black" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {movement.deleted && (
            <div
              data-testid="movement-fix-deleted-banner"
              className="nb-card-sm p-3 flex items-start gap-2 bg-[#FCE4E4] border-[#8A1F1F]"
            >
              <Trash2
                size={16}
                strokeWidth={2.5}
                className="text-[#8A1F1F] shrink-0 mt-0.5"
              />
              <div className="text-xs text-[#5A0F0F] flex-1">
                Deleted on{" "}
                <b>
                  {movement.deleted_at
                    ? new Date(movement.deleted_at).toLocaleDateString([], {
                        dateStyle: "medium",
                      })
                    : "—"}
                </b>{" "}
                by <b>{movement.deleted_by || "—"}</b>. This movement is excluded from all reports.
                Restore to bring it back and re-apply {movement.qty} {movement.unit} to stock.
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label>
              <div className="label-eyebrow mb-1">Quantity ({movement.unit})</div>
              <input
                data-testid="movement-fix-qty-input"
                autoFocus
                type="number"
                step="0.01"
                min="0"
                disabled={movement.deleted}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                  if (e.key === "Escape") onClose?.();
                }}
                className="nb-input font-mono-num text-lg disabled:bg-[#F4F8FB] disabled:opacity-60"
              />
              {qtyChanged && (
                <div className="label-eyebrow mt-1 text-[#5B3A99]">
                  was {movement.qty}
                </div>
              )}
            </label>

            <label>
              <div className="label-eyebrow mb-1">Unit cost ($)</div>
              <input
                data-testid="movement-fix-cost-input"
                type="number"
                step="0.01"
                min="0"
                disabled={movement.deleted}
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                  if (e.key === "Escape") onClose?.();
                }}
                className="nb-input font-mono-num text-lg disabled:bg-[#F4F8FB] disabled:opacity-60"
              />
              {costChanged && (
                <div className="label-eyebrow mt-1 text-[#5B3A99]">
                  was {money(movement.unit_cost)}
                </div>
              )}
            </label>
          </div>

          {/* Live line-total preview */}
          <div className="nb-card-sm p-3 flex items-center justify-between bg-[#DFEEF7]">
            <div>
              <div className="label-eyebrow">Line total (preview)</div>
              <div
                data-testid="movement-fix-line-preview"
                className="font-mono-num font-bold text-2xl"
              >
                {money(nextLine)}
              </div>
            </div>
            {originalLine !== nextLine && Number.isFinite(nextLine) && (
              <div className="text-right">
                <div className="label-eyebrow text-[#607087]">was</div>
                <div className="font-mono-num text-sm text-[#607087] line-through">
                  {money(originalLine)}
                </div>
              </div>
            )}
          </div>

          {/* Back-date + machine hours row. Back-date lets managers correct
              the timeline when a staff member forgot to log a take on the
              actual day; hours captures the meter reading at that moment. */}
          <div className="grid grid-cols-2 gap-3">
            <label>
              <div className="label-eyebrow mb-1">Date of movement</div>
              <input
                data-testid="movement-fix-date-input"
                type="date"
                disabled={movement.deleted}
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="nb-input font-mono-num text-sm disabled:bg-[#F4F8FB] disabled:opacity-60"
              />
              {dateChanged && (
                <div className="label-eyebrow mt-1 text-[#5B3A99]">
                  was{" "}
                  {new Date(movement.created_at).toLocaleDateString([], {
                    dateStyle: "medium",
                  })}
                </div>
              )}
            </label>
            <label>
              <div className="label-eyebrow mb-1">Machine hours (optional)</div>
              <input
                data-testid="movement-fix-hours-input"
                type="number"
                step="0.1"
                min="0"
                inputMode="decimal"
                placeholder="Meter reading"
                disabled={movement.deleted}
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="nb-input font-mono-num text-sm disabled:bg-[#F4F8FB] disabled:opacity-60"
              />
              {hoursChanged && (
                <div className="label-eyebrow mt-1 text-[#5B3A99]">
                  was {originalHours === "" ? "—" : originalHours}
                </div>
              )}
            </label>
          </div>

          {dateChanged && (
            <div className="nb-card-sm p-3 flex items-start gap-2 bg-[#FFF6E0] border-[#B8891C]">
              <AlertTriangle
                size={16}
                strokeWidth={2.5}
                className="text-[#B8891C] shrink-0 mt-0.5"
              />
              <div className="text-xs text-[#5C4A0F]">
                Back-dating changes when this movement appears on reports and
                the usage timeline — not stock on-hand. Time-of-day is
                preserved from the original entry.
              </div>
            </div>
          )}

          {qtyChanged && (
            <div className="nb-card-sm p-3 flex items-start gap-2 bg-[#FFF6E0] border-[#B8891C]">
              <AlertTriangle
                size={16}
                strokeWidth={2.5}
                className="text-[#B8891C] shrink-0 mt-0.5"
              />
              <div className="text-xs text-[#5C4A0F]">
                Changing quantity will also adjust <b>{movement.stock_name}</b>&apos;s current stock by{" "}
                {movement.action === "IN" ? "+" : "-"}
                {(Number(qty) - Number(movement.qty)).toFixed(2)} {movement.unit} to keep the balance in sync.
              </div>
            </div>
          )}

          {/* Audit trail — every prior correction on this movement, newest last */}
          {movement.audit_log?.length > 0 && (
            <div
              data-testid="movement-fix-audit-log"
              className="nb-card-sm p-3 bg-[#F4F8FB]"
            >
              <div className="label-eyebrow mb-2">Correction history</div>
              <div className="flex flex-col gap-1.5 max-h-32 overflow-y-auto">
                {movement.audit_log.map((entry, idx) => (
                  <div
                    key={idx}
                    className="text-xs flex items-baseline gap-2"
                  >
                    <span className="font-mono-num text-[#607087] shrink-0">
                      {new Date(entry.at).toLocaleDateString([], {
                        dateStyle: "short",
                      })}
                    </span>
                    <span className="font-display shrink-0">{entry.user_name || "—"}</span>
                    <span className="text-[#5B3A99] break-all">
                      {Object.entries(entry.changes || {}).map(([field, delta], i, arr) => (
                        <span key={field}>
                          {field}: <b>{delta.from ?? "—"}</b> → <b>{delta.to ?? "—"}</b>
                          {i < arr.length - 1 ? ", " : ""}
                        </span>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t-2 border-black flex justify-between items-center gap-2 bg-[#F4F8FB] rounded-b-[22px] flex-wrap">
          {movement.deleted ? (
            <button
              data-testid="movement-fix-restore"
              onClick={restoreMovement}
              disabled={deleting}
              className="nb-btn bg-[#5FBE7E] text-white px-4 py-2 flex items-center gap-2"
            >
              <RotateCcw size={14} strokeWidth={2.5} />
              {deleting ? "Restoring…" : "Restore movement"}
            </button>
          ) : (
            <button
              data-testid="movement-fix-delete"
              onClick={deleteMovement}
              disabled={deleting || saving}
              className="nb-btn bg-[#FCE4E4] text-[#8A1F1F] border-[#8A1F1F] px-4 py-2 flex items-center gap-2"
              title="Soft-delete this movement and roll back stock"
            >
              <Trash2 size={14} strokeWidth={2.5} />
              {deleting ? "Deleting…" : "Delete"}
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button
              onClick={onClose}
              className="nb-btn bg-white px-4 py-2"
              disabled={saving || deleting}
            >
              {movement.deleted ? "Close" : "Cancel"}
            </button>
            {!movement.deleted && (
              <button
                data-testid="movement-fix-save"
                onClick={submit}
                disabled={saving || deleting || (!qtyChanged && !costChanged && !dateChanged && !hoursChanged)}
                className="nb-btn nb-btn-success px-4 py-2 flex items-center gap-2"
              >
                <Save size={14} strokeWidth={2.5} />
                {saving ? "Saving…" : "Save"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovementFixDialog;
